<template>
    <v-dialog
        :value="isOpen"
        max-width="900px"
        transition="slide-y-transition"
        origin="top center"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="isNewShipmentsOrderPosition">
                        Dodaj adres
                    </template>
                    <template v-else>
                        Edycja adresu
                    </template>
                </h1>
            </v-card-title>

            <div class="mx-5 my-1">
                <v-row>
                    <v-col cols="12">
                        <v-select
                            v-model="customerContact"
                            :items="customerContacts"
                            label="Wybierz z dostępnych adresów"
                            return-object
                            item-text="companyName"
                            outlined
                            dense
                            hide-details
                            no-data-text="Brak adresów"
                            class="mb-6"
                            clearable
                            :disabled="isTransportEditableOnly"
                            @input="saveTemplate"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field
                            v-model="shippingOrderPosition.companyName"
                            label="Nazwa firmy"
                            :error="companyNameErrors"
                            outlined
                            dense
                            :disabled="isPersonalPickup || isTransportEditableOnly"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-text-field
                            v-model="shippingOrderPosition.contactPerson"
                            label="Osoba kontaktowa"
                            :error="contactPersonErrors"
                            outlined
                            dense
                            :disabled="isPersonalPickup || isTransportEditableOnly"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="shippingOrderPosition.street"
                            label="Ulica"
                            :error="streetErrors"
                            outlined
                            dense
                            :disabled="isPersonalPickup || isTransportEditableOnly"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="shippingOrderPosition.postCode"
                            label="Kod pocztowy"
                            :error="postCodeErrors"
                            outlined
                            dense
                            :disabled="isPersonalPickup || isTransportEditableOnly"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field
                            v-model="shippingOrderPosition.city"
                            label="Miasto"
                            outlined
                            dense
                            :error="cityErrors"
                            :disabled="isPersonalPickup || isTransportEditableOnly"
                        />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-select
                            :value="shippingOrderPosition.countryId"
                            :items="countries"
                            item-value="id"
                            item-text="name"
                            no-data-text="Nie znaleziono kraju"
                            label="Kraj"
                            outlined
                            dense
                            :error="countryIdErrors"
                            :disabled="isPersonalPickup || isTransportEditableOnly"
                            @change="changeCountry(shippingOrderPosition, $event)"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="4">
                        <v-datetime-picker
                            ref="expeditionDateTimePicker"
                            v-model="expeditionDateTime"
                            label="Planowana data wysyłki"
                            date-format="dd.MM.yyyy"
                            :date-picker-props="expeditionDateTimeProps.dateProps"
                            :text-field-props="expeditionDateTimeProps.textFieldProps"
                            :time-picker-props="expeditionDateTimeProps.timeProps"
                            :disabled="isTransportEditableOnly"
                            @input="expeditionDateTimeHandler"
                        >
                            <template slot="dateIcon">
                                <v-icon>mdi-calendar</v-icon>
                            </template>
                            <template slot="timeIcon">
                                <v-icon>mdi-clock-outline</v-icon>
                            </template>
                            <template slot="actions" slot-scope="{parent}">
                                <v-btn text color="error" @click.native="parent.clearHandler">
                                    Wyczyść
                                </v-btn>
                                <v-btn text color="success" @click="parent.okHandler">
                                    Zapisz
                                </v-btn>
                            </template>
                        </v-datetime-picker>
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-datetime-picker
                            ref="deliveryDateTimePicker"
                            v-model="deliveryDateTime"
                            label="Data dostawy"
                            date-format="dd.MM.yyyy"
                            :date-picker-props="deliveryDateTimeProps.dateProps"
                            :text-field-props="deliveryDateTimeProps.textFieldProps"
                            :time-picker-props="deliveryDateTimeProps.timeProps"
                            :disabled="isTransportEditableOnly"
                            @input="deliveryDateTimeHandler"
                        >
                            <template slot="dateIcon">
                                <v-icon>mdi-calendar</v-icon>
                            </template>
                            <template slot="timeIcon">
                                <v-icon>mdi-clock-outline</v-icon>
                            </template>
                            <template slot="actions" slot-scope="{parent}">
                                <v-btn text color="error" @click.native="parent.clearHandler">
                                    Wyczyść
                                </v-btn>
                                <v-btn text color="success" @click="parent.okHandler">
                                    Zapisz
                                </v-btn>
                            </template>
                        </v-datetime-picker>
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            ref="phoneNumber"
                            v-model="shippingOrderPosition.phoneNumber"
                            v-mask="'(+##) #########'"
                            label="Telefon"
                            outlined
                            dense
                            :error="phoneNumberErrors"
                            :disabled="isPersonalPickup || isTransportEditableOnly"
                        />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="4">
                        <v-select
                            v-model="shippingOrderPosition.deliveryMethodId"
                            :items="availableDeliveryMethods"
                            label="Środek transportu"
                            item-value="id"
                            item-text="name"
                            outlined
                            dense
                            :error="deliveryMethodIdErrors"
                            @input="updateAddress($event, shippingOrderPosition)"
                            @change="handleDeliveryMethodChange"
                        />
                    </v-col>
                    <v-col cols="6" sm="4">
                        <v-text-field
                            v-model="shippingOrderPosition.distance"
                            label="Dystans"
                            suffix="km"
                            :hint="shippingOrderPositionDistanceHint()"
                            outlined
                            dense
                            :error="distanceErrors"
                            @input="handleDistance"
                        />
                    </v-col>
                    <v-col cols="6" sm="4">
                        <v-btn
                            style="width: 100%;"
                            color="success"
                            data-sentry-label="Oblicz"
                            :disabled="isCalculateDeliveryCostDisabled"
                            @click="calculateDeliveryCost"
                        >
                            Oblicz koszt wysyłki
                        </v-btn>
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field
                            v-model="shippingOrderPosition.cost"
                            label="Koszty dostawy"
                            outlined
                            dense
                            :hide-details="!transportCostError"
                            :error="!!transportCostError"
                            :hint="transportCostError"
                            @change="transportCostChange"
                        />
                    </v-col>
                </v-row>
            </div>

            <template>
                <v-divider />

                <v-card-actions>
                    <v-btn
                        color="error"
                        text
                        data-sentry-label="Zamknij"
                        @click="onClose"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        color="success"
                        text
                        :data-sentry-label="isNewShipmentsOrderPosition ? 'Dodaj' : 'Zapisz'"
                        :disabled="isSaveDisabled"
                        @click="onSave"
                    >
                        <template v-if="isNewShipmentsOrderPosition">
                            Dodaj
                        </template>
                        <template v-else>
                            Zapisz
                        </template>
                    </v-btn>
                </v-card-actions>
            </template>
        </v-card>
    </v-dialog>
</template>

<script>
import parse from '@/mixins/parse.js'

import { required, decimal } from 'vuelidate/lib/validators'

import { isEmpty, findIndex } from 'lodash'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'ShipmentsOrderPositionDialog',
    mixins: [ parse ],
    props: {
        isOpen: {
            type: Boolean
        },
        customerContacts: {
            type: Array,
            default: () => []
        },
        headquartersContact: {
            type: Object,
            default: null
        },
        transportTypesData: {
            type: Array,
            default: () => []
        },
        shippingOrderPositionData: {
            type: Object,
            default: null
        },
        validations: {
            type: Object,
            default: null
        },
        isTransportEditableOnly: {
            type: Boolean
        },
        totalWeight: {
            type: Number,
            default: 0
        },
        deliveryMethods: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            customer: {
                contacts: []
            },
            customerContact: null,
            shippingOrderPosition: {},
            deliveryDateTime: null,
            deliveryDateTimeProps: {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    prependIcon: 'mdi-calendar',
                    error: false
                },
                dateProps: {
                    firstDayOfWeek: 1
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            expeditionDateTime: null,
            expeditionDateTimeProps: {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    prependIcon: 'mdi-calendar',
                    error: false
                },
                dateProps: {
                    firstDayOfWeek: 1
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            wasPersonalPickupSelectedPreviously: false,
            bufferedShippingOrderPosition: {},
        }
    },
    validations() {
        const mustNotBeSelectedValues = (value) => !['', '-', null, '.'].includes(value)

        return {
            shippingOrderPosition: {
                companyName: {
                    required
                },
                contactPerson: {
                    required,
                    mustNotBeSelectedValues,
                },
                street: {
                    required,
                    mustNotBeSelectedValues,
                },
                postCode: {
                    required,
                    mustNotBeSelectedValues,
                },
                city: {
                    required,
                    mustNotBeSelectedValues,
                },
                countryId: {
                    required
                },
                phoneNumber: {
                    required,
                    mustNotBeSelectedValues,
                },
                deliveryMethodId: {
                    required
                },
                distance: {
                    required
                },
                cost: {
                    required,
                    decimal
                },
            },
            expeditionDateTime: {
                required
            },
            deliveryDateTime: {
                required
            }
        }
    },
    computed: {
        isNewShipmentsOrderPosition() {
            return isEmpty(this.shippingOrderPositionData)
        },
        countries() {
            return this.$store.state.customers.customers.countries
        },
        companyNameErrors() {
            return this.$v.shippingOrderPosition.companyName.$invalid
        },
        contactPersonErrors() {
            return this.$v.shippingOrderPosition.contactPerson.$invalid
        },
        streetErrors() {
            return this.$v.shippingOrderPosition.street.$invalid
        },
        postCodeErrors() {
            return this.$v.shippingOrderPosition.postCode.$invalid
        },
        cityErrors() {
            return this.$v.shippingOrderPosition.city.$invalid
        },
        countryIdErrors() {
            return this.$v.shippingOrderPosition.countryId.$invalid
        },
        phoneNumberErrors() {
            return this.$v.shippingOrderPosition.phoneNumber.$invalid
        },
        deliveryMethodIdErrors() {
            return this.$v.shippingOrderPosition.deliveryMethodId.$invalid
        },
        distanceErrors() {
            return this.$v.shippingOrderPosition.distance.$invalid
        },
        costErrors() {
            return this.$v.shippingOrderPosition.cost.$invalid || !this.$v.shippingOrderPosition.cost.decimal
        },
        isPersonalPickup() {
            return this.shippingOrderPosition.deliveryMethodId === 20
        },
        distance() {
            return this.shippingOrderPosition ? this.shippingOrderPosition.distance : ''
        },
        transportCost() {
            return this.shippingOrderPosition ? this.shippingOrderPosition.cost : ''
        },
        isCalculateDeliveryCostDisabled() {
            return !this.shippingOrderPosition?.deliveryMethodId || this.shippingOrderPosition?.distance === '' || this.shippingOrderPosition?.distance === null
        },
        transportCostError() {
            const regex = /^\d*(\.\d{0,2})?$/
            const value = this.transportCost

            if (value === null || value === '' || value === undefined) {
                return ''
            }
            if (!regex.test(value.toString())) {
                return 'Maksymalnie 2 cyfry po przecinku'
            }

            return ''
        },
        isSaveDisabled() {
            return !!this.transportCostError
        },
        isCustomerTransport() {
            return this.shippingOrderPosition.deliveryMethodId === 20
        },
        isCourierTransport() {
            return this.shippingOrderPosition.deliveryMethodId === 22
        },
        availableDeliveryMethods() {
            const available = this.deliveryMethods.filter(item => item.isAvailableInTransportSelection)
            const currentId = this.shippingOrderPositionData && this.shippingOrderPositionData.deliveryMethodId
            let list = available

            if (currentId && !available.some(item => item.id === currentId)) {
                const current = this.deliveryMethods.find(item => item.id === currentId)

                if (current) {
                    list = [...available, current]
                }
            }

            return list
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                if (!this.isNewShipmentsOrderPosition) {
                    this.shippingOrderPosition = JSON.parse(JSON.stringify(this.shippingOrderPositionData))
                    this.expeditionDateTime = this.shippingOrderPosition.expeditionDateTime
                        ? new Date(this.shippingOrderPosition.expeditionDateTime)
                        : null
                    this.deliveryDateTime = this.shippingOrderPosition.deliveryDateTime
                        ? new Date(this.shippingOrderPosition.deliveryDateTime)
                        : null
                }
            }
        },
        shippingOrderPosition: {
            handler: function(value) {
                this.wasPersonalPickupSelectedPreviously = this.shippingOrderPosition.deliveryMethodId === 20

                if (value.deliveryMethodId === 20) {
                    return
                }
                let { transportType, ...rest } = value

                this.bufferedShippingOrderPosition = { ...this.bufferedShippingOrderPosition, ...rest }
            },
            deep: true
        },
    },
    methods: {
        changeCountry(shippingOrderPosition, countryId) {
            this.$set(shippingOrderPosition, 'countryId', countryId)
            const index = findIndex(this.countries, { 'id': countryId })

            this.$set(shippingOrderPosition, 'country', this.countries[index])
        },
        changePhoneNumber(shippingOrderPosition, phoneNumber) {
            this.$set(shippingOrderPosition, 'phoneNumber', phoneNumber)
        },
        expeditionDateTimeHandler(date) {
            this.validateExpeditionTemplateDateTimeDate()
            this.expeditionDateTime = date
            date ? this.shippingOrderPosition.expeditionDateTime = date.toISOString() : this.shippingOrderPosition.expeditionDateTime = null
        },
        deliveryDateTimeHandler(date) {
            this.validateDeliveryDateTimeDate()
            this.deliveryDateTime = date
            date ? this.shippingOrderPosition.deliveryDateTime = date.toISOString() : this.shippingOrderPosition.deliveryDateTime = null
        },
        validateExpeditionTemplateDateTimeDate() {
            this.expeditionDateTimeProps.textFieldProps.error = false

            if (this.$v.expeditionDateTime.$invalid) this.expeditionDateTimeProps.textFieldProps.error = true
        },
        validateDeliveryDateTimeDate() {
            this.deliveryDateTimeProps.textFieldProps.error = false

            if (this.$v.deliveryDateTime.$invalid) this.deliveryDateTimeProps.textFieldProps.error = true
        },
        saveTemplate() {
            if (!this.customerContact) return

            this.shippingOrderPosition = {
                ...this.shippingOrderPosition,
                addressType: this.customerContact.addressType,
                city: this.customerContact.city,
                companyName: this.customerContact.companyName,
                countryId: this.customerContact.countryId,
                country: this.customerContact.country,
                postCode: this.customerContact.postCode,
                street: this.customerContact.street,
                deliveryMethodId: this.isCustomerTransport ? this.shippingOrderPosition.deliveryMethodId : null,
                contactPerson: this.customerContact && this.customerContact.contacts.length > 0 ? this.parseCustomerContact(this.customerContact.contacts[0]) : null
            }
            const phoneNumber = this.$refs.phoneNumber

            this.customerContact && this.customerContact.contacts.length > 0 ? phoneNumber.$refs.input.value = this.customerContact.contacts[0].phoneNumber : null
        },
        updateAddress(value, position) {
            if (this.isCustomerTransport) {
                this.customerContact = null
                let tmpBuffer = {...this.bufferedShippingOrderPosition}

                this.shippingOrderPosition = {
                    ...this.shippingOrderPosition,
                    addressType: this.headquartersContact.addressType,
                    city: this.headquartersContact.city,
                    companyName: this.headquartersContact.companyName,
                    countryId: this.headquartersContact.countryId,
                    country: this.headquartersContact.country,
                    postCode: this.headquartersContact.postCode,
                    street: this.headquartersContact.street,
                    contactPerson: this.headquartersContact && this.headquartersContact.contacts.length > 0 ?
                        this.headquartersContact.contacts[0].firstName : null
                }
                const phoneNumber = this.$refs.phoneNumber

                this.headquartersContact && this.headquartersContact.contacts.length > 0 ? phoneNumber.$refs.input.value = this.headquartersContact.contacts[0].phoneNumber : null
                this.bufferedShippingOrderPosition = tmpBuffer
            }
            if (!this.isPersonalPickup && this.wasPersonalPickupSelectedPreviously) {
                this.customerContact = null
                this.shippingOrderPosition = {
                    ...this.bufferedShippingOrderPosition,
                    index: position.index,
                    shippingOrderPositionPackages: position.shippingOrderPositionPackages,
                    transportType: value
                }
                const phoneNumber = this.$refs.phoneNumber

                phoneNumber.$refs.input.value = null
            }
        },
        onSave() {
            this.$v.$touch()
            this.validateExpeditionTemplateDateTimeDate()
            this.validateDeliveryDateTimeDate()

            let payload = JSON.parse(JSON.stringify(this.shippingOrderPosition))

            if (this.isNewShipmentsOrderPosition) {
                payload.shippingOrderPositionPackages = []
            }

            this.$emit('on-save', payload)
            this.onClose()
        },
        onClose() {
            this.$emit('on-close')

            setTimeout(() => {
                this.shippingOrderPosition = {}
                this.customerContact = null
                this.expeditionDateTime = null
                this.deliveryDateTime = null
                this.bufferedShippingOrderPosition = {}
            }, 500)

            this.$refs.expeditionDateTimePicker.clearHandler()
            this.$refs.deliveryDateTimePicker.clearHandler()
            this.$v.$reset()
            this.deliveryDateTimeProps.textFieldProps.error = false
            this.expeditionDateTimeProps.textFieldProps.error = false
        },
        shippingOrderPositionDistanceHint() {
            if (!this.$v.shippingOrderPosition.distance.required) return 'Proszę wpisać dystans dostawy - sumę w obie strony'
        },
        handleDeliveryMethodChange(value) {
            const selectedDeliveryMethod = this.deliveryMethods.find(item => item.id === value)

            this.shippingOrderPosition.deliveryMethod = selectedDeliveryMethod
            
            this.clearTransportDistance()
            this.clearTransportCost()
        },
        setTransportCostValue() {
            const cost = this.calculateCost(this.kilometers, this.factor)

            this.handleCost(cost)
        },
        clearTransportDistance() {
            this.shippingOrderPosition.distance =
            this.isCustomerTransport || this.isCourierTransport
                ? 0 : ''
        },
        clearTransportCost() {
            this.shippingOrderPosition.cost =
            this.isCustomerTransport || this.isCourierTransport
                ? 0 : ''
        },
        transportCostChange() {
            this.handleCost(parseFloat(this.transportCost) || 0)
        },
        calculateCost(distance, factor) {
            return Math.ceil(parseInt(distance) * factor).toString()

        },
        handleDistance(value) {
            value = value ? value : ''
            this.shippingOrderPosition.distance = value
            this.kilometers = value
        },
        handleCost(value) {
            this.shippingOrderPosition.cost = value
        },
        async calculateDeliveryCost() {
            const payload = {
                deliveryMethodId: this.shippingOrderPosition.deliveryMethodId,
                distance: parseInt(this.distance),
                weight: this.totalWeight,
                packagesQuantities: this.shippingOrderPosition.shippingOrderPositionPackages.map(item => ({
                    packageTypeId: item.packageTypeId,
                    quantity: item.amount,
                })),
            }

            return await this.$store.dispatch('calculations/calculateDeliveryCost', payload)
                .then(response => {
                    this.handleCost(response.data.price)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
    }
}
</script>